import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { POExtractionStatus } from './POExtractionStatus';
import { POFundingSummary } from './POFundingSummary';
import { POLineItemsTable } from './POLineItemsTable';
import { POSkuMappingReview } from './POSkuMappingReview';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import POPdfViewerDialog from './POPdfViewerDialog';
import { Package, Check, ChevronsUpDown, FileText } from 'lucide-react';
import { usePOExtractionJob } from '@/hooks/usePOExtraction';
import { usePOLineItems } from '@/hooks/usePOLineItems';
import { useRecalculatePOFunding } from '@/hooks/useRecalculatePOFunding';
import { cn } from '@/lib/utils';
interface PurchaseOrderFile {
  id: string;
  file_name: string;
  file_type?: string | null;
  storage_path?: string;
}

interface PurchaseOrder {
  id: string;
  po_number: string;
  manufacturer_name: string | null;
  vendor_name: string;
  status: string;
  order_date: string;
  total_amount: number | null;
  files?: PurchaseOrderFile[];
}

interface POLineItemsViewProps {
  purchaseOrders: PurchaseOrder[];
  organisationId: string;
  isAdmin?: boolean;
  isResellerPortal?: boolean;
  initialSelectedPOId?: string | null;
}

function SelectedPOContent({
  selectedPO,
  selectedFileId,
  selectedFileType,
  organisationId,
  extractionJob,
  isLoadingExtractionJob,
  userEmail,
  isResellerPortal,
}: {
  selectedPO: PurchaseOrder;
  selectedFileId: string;
  selectedFileType?: string | null;
  organisationId: string;
  extractionJob: ReturnType<typeof usePOExtractionJob>['data'];
  isLoadingExtractionJob: boolean;
  userEmail: string;
  isResellerPortal?: boolean;
}) {
  const { isRecalculating } = useRecalculatePOFunding({
    purchaseOrderId: selectedPO.id,
    fileId: selectedFileId,
    organisationId,
    enabled: extractionJob?.status === 'completed',
  });

  const { data: lineItems = [] } = usePOLineItems(selectedPO.id);

  const showLineItems =
    selectedPO.status !== 'archived' &&
    (extractionJob?.status === 'completed' || lineItems.length > 0);

  return (
    <>
      {/* Extraction Status */}
      <POExtractionStatus
        fileId={selectedFileId}
        purchaseOrderId={selectedPO.id}
        organisationId={organisationId}
        fileType={selectedFileType}
        extractionJob={extractionJob}
        isLoading={isLoadingExtractionJob}
      />

      {isRecalculating && lineItems.length > 0 && (
        <p className="text-sm text-muted-foreground">Updating funding values…</p>
      )}

      {showLineItems && (
        <>
          {/* SKU Mapping Review */}
          {!isResellerPortal && (
            <POSkuMappingReview
              purchaseOrderId={selectedPO.id}
              organisationId={organisationId}
              lineItems={lineItems}
              userEmail={userEmail}
            />
          )}

          {/* Funding Summary */}
          <POFundingSummary purchaseOrderId={selectedPO.id} extractionJob={extractionJob} />

          {/* Line Items Table */}
          <POLineItemsTable
            purchaseOrderId={selectedPO.id}
            userEmail={userEmail}
            poTotal={selectedPO.total_amount}
            extractionJob={extractionJob}
            poFileName={selectedPO.files?.[0]?.file_name}
          />
        </>
      )}
    </>
  );
}

function POSelector({
  purchaseOrders,
  selectedPOId,
  onSelect,
  onViewPdf,
}: {
  purchaseOrders: PurchaseOrder[];
  selectedPOId: string | null;
  onSelect: (id: string) => void;
  onViewPdf?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedPO = purchaseOrders.find((po) => po.id === selectedPOId);
  const selectedLabel = selectedPO
    ? `${selectedPO.files?.[0]?.file_name || selectedPO.po_number} - ${new Date(selectedPO.order_date).toLocaleDateString()}`
    : 'Select a purchase order';

  // Group POs by month/year
  const groupedPOs = useMemo(() => {
    const groups = new Map<string, PurchaseOrder[]>();
    const sorted = [...purchaseOrders].sort(
      (a, b) => new Date(b.order_date).getTime() - new Date(a.order_date).getTime()
    );
    for (const po of sorted) {
      const d = new Date(po.order_date);
      const key = `${d.toLocaleString('default', { month: 'long' })} ${d.getFullYear()}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(po);
    }
    return Array.from(groups.entries());
  }, [purchaseOrders]);

  // Filter by search
  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groupedPOs;
    const q = search.toLowerCase();
    return groupedPOs
      .map(([month, pos]) => {
        const filtered = pos.filter((po) => {
          const fileName = po.files?.[0]?.file_name || '';
          return (
            fileName.toLowerCase().includes(q) ||
            po.po_number.toLowerCase().includes(q) ||
            po.vendor_name.toLowerCase().includes(q)
          );
        });
        return [month, filtered] as [string, PurchaseOrder[]];
      })
      .filter(([, pos]) => pos.length > 0);
  }, [groupedPOs, search]);

  return (
    <div className="flex items-center gap-4">
      <label className="text-sm font-medium">Select Purchase Order:</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full max-w-md justify-between font-normal"
          >
            <span className="truncate">{selectedLabel}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[420px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search by file name, PO number..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList className="max-h-[300px]">
              <CommandEmpty>No purchase orders found.</CommandEmpty>
              {filteredGroups.map(([month, pos]) => (
                <CommandGroup key={month} heading={month}>
                  {pos.map((po) => {
                    const fileName = po.files?.[0]?.file_name || po.po_number;
                    const purchaseDate = new Date(po.order_date).toLocaleDateString();
                    return (
                      <CommandItem
                        key={po.id}
                        value={po.id}
                        className="whitespace-normal break-all"
                        onSelect={() => {
                          onSelect(po.id);
                          setOpen(false);
                          setSearch('');
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4 shrink-0',
                            selectedPOId === po.id ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        {fileName} - {purchaseDate}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {onViewPdf && (
        <Button variant="outline" size="sm" onClick={onViewPdf}>
          <FileText className="h-4 w-4 mr-1" />
          View PDF
        </Button>
      )}
    </div>
  );
}

const POLineItemsView: React.FC<POLineItemsViewProps> = ({
  purchaseOrders,
  organisationId,
  isAdmin = false,
  isResellerPortal = false,
  initialSelectedPOId,
}) => {
  const [selectedPOId, setSelectedPOId] = useState<string | null>(
    initialSelectedPOId || (purchaseOrders.length > 0 ? purchaseOrders[0].id : null)
  );
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

  // Sync when initialSelectedPOId changes (e.g. clicking "View Line Items" on a different PO)
  useEffect(() => {
    if (initialSelectedPOId) {
      setSelectedPOId(initialSelectedPOId);
    }
  }, [initialSelectedPOId]);

  const selectedPO = purchaseOrders.find(po => po.id === selectedPOId);
  const selectedFile = selectedPO?.files?.[0];
  
  // Fetch extraction job for the selected file
  const { data: extractionJob, isLoading: isLoadingExtractionJob } = usePOExtractionJob(selectedFileId);
  const queryClient = useQueryClient();
  const prevStatusRef = useRef<string | null>(null);

  // Auto-refresh line items when extraction completes
  useEffect(() => {
    const currentStatus = extractionJob?.status ?? null;
    if (
      prevStatusRef.current &&
      prevStatusRef.current !== 'completed' &&
      currentStatus === 'completed' &&
      selectedPOId
    ) {
      queryClient.invalidateQueries({ queryKey: ['po-line-items', selectedPOId] });
    }
    prevStatusRef.current = currentStatus;
  }, [extractionJob?.status, selectedPOId, queryClient]);

  // Get user email for verification
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserEmail(data.user?.email || '');
    });
  }, []);

  // Update selected file when PO changes
  useEffect(() => {
    if (selectedPO?.files?.[0]) {
      setSelectedFileId(selectedPO.files[0].id);
    } else {
      setSelectedFileId(null);
    }
  }, [selectedPOId, selectedPO]);

  // Build PDF viewer URL
  const pdfViewerUrl = useMemo(() => {
    if (!selectedFile || !selectedPOId || !selectedFile.storage_path) return null;
    return `/api/serve-po-file?path=${encodeURIComponent(selectedFile.storage_path)}&poId=${encodeURIComponent(selectedPOId)}`;
  }, [selectedFile, selectedPOId]);

  if (purchaseOrders.length === 0) {
    return (
      <div className="py-8">
        <div className="text-center text-muted-foreground">
          <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No purchase orders available</p>
          <p className="text-sm mt-1">Upload purchase orders to view line items</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* PO Selector */}
      <POSelector
        purchaseOrders={purchaseOrders}
        selectedPOId={selectedPOId}
        onSelect={setSelectedPOId}
        onViewPdf={pdfViewerUrl ? () => setPdfDialogOpen(true) : undefined}
      />

      {/* PDF Viewer Dialog */}
      <POPdfViewerDialog
        open={pdfDialogOpen}
        onOpenChange={setPdfDialogOpen}
        url={pdfViewerUrl}
        title={selectedFile?.file_name || 'Purchase Order PDF'}
      />

      {selectedPO && selectedFileId && (
        <SelectedPOContent
          selectedPO={selectedPO}
          selectedFileId={selectedFileId}
          selectedFileType={selectedPO.files?.[0]?.file_type}
          organisationId={organisationId}
          extractionJob={extractionJob}
          isLoadingExtractionJob={isLoadingExtractionJob}
          userEmail={userEmail}
          isResellerPortal={isResellerPortal}
        />
      )}

      {selectedPO && !selectedFileId && (
        <div className="text-center py-8 text-muted-foreground">
          <p>No files found for this purchase order</p>
          <p className="text-sm mt-1">Upload a PDF file to extract line items</p>
        </div>
      )}
    </div>
  );
};

export default POLineItemsView;
